import React, {useEffect, useRef, useState} from 'react';
import {observer} from 'mobx-react-lite';
import CompanyEdit from '../CompanyEdit';
import newStore from '../../storage/NewStore';
import appStore from '../../storage/AppStore';
import PageLayout from '../PageLayout';
import Btn from '../../ui/Btn';
import systemStore from '../../storage/SystemStore';
import {getProduct} from '../../common/config';
import '../../common/company_registration.css';

const product = getProduct();
const BOT_USERNAME = (process.env.REACT_APP_TELEGRAM_BOT_USERNAME || (product === 'club1' ? 'clubnumberone_bot' : '')).replace(/^@/, '');
const BOT_URL = BOT_USERNAME ? `https://t.me/${BOT_USERNAME}?start=register` : '';

const ClientNewCompany = () => {
  const [sharing, setSharing] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  const refreshTimer = useRef(null);
  const user = appStore.user;
  const telegram = typeof window !== 'undefined' ? window.Telegram?.WebApp : null;
  const isMiniApp = Boolean(telegram?.initData);
  const isAuthenticated = Number(user?.id) > 0;
  const hasPhone = Boolean(String(user?.phone_number || '').trim());

  useEffect(() => {
    if (!isAuthenticated || !hasPhone) return;
    newStore.resetCompany();
    newStore.company.setProperty('owner_phone', user.phone_number);
    newStore.checkPendingCompanies();
  }, [isAuthenticated, hasPhone, user?.phone_number]);

  useEffect(() => () => {if (refreshTimer.current) clearTimeout(refreshTimer.current);}, []);

  const openBot = () => {
    if (!BOT_URL) return;
    if (isMiniApp && telegram?.openTelegramLink) telegram.openTelegramLink(BOT_URL);
    else window.open(BOT_URL, '_blank', 'noopener,noreferrer');
  };

  const refreshProfile = async (attempt = 0) => {
    try {
      await appStore.init({minimal: true});
      if (appStore.user?.phone_number) {setSharing(false); return;}
    } catch (error) {
      console.error('Company registration phone refresh:', error);
    }
    if (attempt < 5) refreshTimer.current = setTimeout(() => refreshProfile(attempt + 1), 1500);
    else setSharing(false);
  };

  const requestPhone = () => {
    if (!telegram?.requestContact) {openBot(); return;}
    setSharing(true);
    telegram.requestContact(status => {
      if (status === 'sent' || status === true) {setContactSent(true); refreshProfile();}
      else setSharing(false);
    });
  };

  const onboarding = (icon, title, description, primaryLabel, onPrimary, secondary = null) => (
    <PageLayout header={<h3 className="m-0">{appStore.tr.add_company}</h3>}>
      <div className="cr-page">
        <div className="cr-gate">
          <div className="cr-gate-icon"><i className={`pi ${icon}`} aria-hidden="true"/></div>
          <h2>{title}</h2>
          <p>{description}</p>
          <div className="cr-gate-actions">
            <button type="button" className="cr-primary" disabled={!onPrimary || sharing} onClick={onPrimary}>
              {sharing && <i className="pi pi-spin pi-spinner" aria-hidden="true"/>}{primaryLabel}
            </button>
            {secondary && <button type="button" className="cr-secondary" onClick={secondary.onClick}>{secondary.label}</button>}
          </div>
        </div>
      </div>
    </PageLayout>
  );

  if (!user?.initialized) return (
    <PageLayout header={<h3 className="m-0">{appStore.tr.add_company}</h3>}>
      <div className="cr-page"><div className="cr-gate"><i className="pi pi-spin pi-spinner" aria-label="Loading"/></div></div>
    </PageLayout>
  );

  if (!isAuthenticated) return onboarding('pi-telegram', appStore.tr.company_registration_telegram_title,
    appStore.tr.company_registration_telegram_text, appStore.tr.company_registration_open_bot, BOT_URL ? openBot : null);

  if (!hasPhone) return onboarding('pi-phone', appStore.tr.company_registration_phone_title,
    contactSent ? appStore.tr.company_registration_phone_wait : appStore.tr.company_registration_phone_text,
    isMiniApp && telegram?.requestContact ? appStore.tr.company_registration_share_phone : appStore.tr.company_registration_open_bot,
    isMiniApp && telegram?.requestContact ? requestPhone : (BOT_URL ? openBot : null),
    contactSent ? {label: appStore.tr.company_registration_refresh, onClick: () => refreshProfile()} : null);

  if (newStore.pending_companies.length > 0) {
    return (
      <PageLayout header={<h3 className="m-0">{appStore.tr.welcome}</h3>}>
        <div className="cr-page">
          <div className="cr-heading"><div className="cr-heading-icon"><i className="pi pi-clock"/></div>
            <div><h2>{appStore.tr.pending}</h2><p>{appStore.tr.pending_text}</p></div>
          </div>
          <div className="cr-pending-list">
            {newStore.pending_companies.map(c => <div className="cr-pending-item" key={c.id}>
              <div className="cr-pending-name"><i className="pi pi-building" aria-hidden="true"/><strong>{c.name}</strong><span className="cr-pending-badge">{appStore.tr.pending}</span></div>
              <Btn icon="pi pi-bell" label={appStore.tr.remind_admin} outlined onClick={() => newStore.remindAdmin(c.id)
                .then(() => systemStore.setSuccess(appStore.tr.reminder_sent))
                .catch(err => systemStore.setError(err.message))}/>
            </div>)}
          </div>
        </div>
      </PageLayout>
    );
  }

  return <CompanyEdit company={newStore.company} role="client"/>;
};

export default observer(ClientNewCompany);
